import {
  createSignal,
  createUniqueId,
  createEffect,
  onCleanup,
  onMount,
  Show,
  type JSX,
} from 'solid-js';

/**
 * App-wide single-open coordination: opening one InfoTip closes any other. Module-level
 * (not a context/provider) so this works standalone anywhere in the tree — no HintCtx
 * ancestor required.
 */
const [openId, setOpenId] = createSignal<string | null>(null);

/**
 * The mobile/keyboard-safe replacement for the old `Hint` (gpu.tsx). Shows an explanatory
 * bubble on hover, click/tap, or keyboard (Enter/Space toggle, Escape close). Default trigger
 * is a small circled "i" marker; pass `label` to use custom trigger content instead (e.g. a
 * dotted-underline metric label).
 */
export function InfoTip(props: { text: string; label?: JSX.Element; class?: string }) {
  const id = createUniqueId();
  const tipId = `infotip-${id}`;
  const [hover, setHover] = createSignal(false);
  const isOpen = () => openId() === id;
  const visible = () => isOpen() || hover();

  let triggerEl: HTMLSpanElement | undefined;
  let bubbleEl: HTMLSpanElement | undefined;
  const [shiftX, setShiftX] = createSignal(0);

  const open = () => setOpenId(id);
  const close = () => {
    if (isOpen()) setOpenId(null);
  };
  const toggle = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isOpen()) setOpenId(null);
    else open();
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
      // preventDefault stops Space from scrolling the page.
      e.preventDefault();
      if (isOpen()) setOpenId(null);
      else open();
    } else if (e.key === 'Escape') {
      close();
    }
  };

  // Outside-click closes. Clicks on this trigger are recognized via the data-infotip
  // marker so the same click that opens/toggles the tip doesn't also get treated as
  // "outside" and immediately close it.
  const onDocumentClick = (e: MouseEvent) => {
    const target = e.target as Element | null;
    if (target?.closest('[data-infotip]') === triggerEl) return;
    if (isOpen()) setOpenId(null);
  };

  onMount(() => {
    document.addEventListener('click', onDocumentClick);
  });
  onCleanup(() => {
    document.removeEventListener('click', onDocumentClick);
  });

  // Edge-aware horizontal clamp: when the bubble becomes visible, measure it against the
  // viewport and shift it left/right so it doesn't clip off narrow/mobile screens. jsdom
  // reports zero-size layout boxes, so this is a no-op (shiftX stays 0) under tests — the
  // open/close/keyboard behavior above is what's actually verified there.
  createEffect(() => {
    if (!visible()) {
      setShiftX(0);
      return;
    }
    const bubble = bubbleEl;
    if (!bubble) return;
    const rect = bubble.getBoundingClientRect();
    const vw = window.innerWidth || 0;
    const margin = 8;
    let delta = 0;
    if (rect.left < margin) delta = margin - rect.left;
    else if (rect.right > vw - margin) delta = vw - margin - rect.right;
    if (delta !== 0) setShiftX(delta);
  });

  return (
    <span class={`relative inline-block ${props.class ?? ''}`}>
      <span
        ref={triggerEl}
        data-infotip
        role="button"
        tabindex={0}
        aria-expanded={isOpen() ? 'true' : 'false'}
        aria-describedby={visible() ? tipId : undefined}
        class={
          props.label
            ? 'cursor-help border-b border-dotted border-current'
            : 'hud-tip'
        }
        onClick={toggle}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onKeyDown={onKeyDown}
      >
        {props.label ?? 'i'}
      </span>
      <Show when={visible()}>
        {/*
         * Positioning and the materialize animation must live on different elements.
         * A CSS animation's keyframed properties always win over an inline style on
         * the SAME element (even past the end of the animation, since it runs with
         * `fill-mode: both`) — so the dynamic `shiftX` clamp below, applied as an
         * inline `transform`, would never actually take effect if it shared an
         * element with `.hud-tip-bubble`'s `animation: hud-materialize` (which also
         * keyframes `transform`). This outer wrapper owns the position and is what
         * `shiftX`'s viewport measurement reads back via `ref`; `.hud-tip-bubble`
         * nested inside owns the animation and keeps the `id`/`role="tooltip"` that
         * `aria-describedby` above points at.
         */}
        <span
          ref={bubbleEl}
          class="pointer-events-none absolute bottom-full left-1/2 z-40 mb-2"
          style={{ transform: `translateX(calc(-50% + ${shiftX()}px))` }}
        >
          <span id={tipId} role="tooltip" class="hud-tip-bubble whitespace-normal normal-case">
            <span class="hud-tip-scan" aria-hidden="true" />
            {props.text}
          </span>
        </span>
      </Show>
    </span>
  );
}
